import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { TID } from "@/constants/testIds";

const heroImg =
  "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/8j3gcrtq_1000439944.jpg";
const secondaryImg =
  "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/3o3ahl3y_1000439943.jpg";

export default function Blog() {
  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="py-24 md:py-32 border-t border-[#E5E1DA] bg-gradient-to-b from-[#F4E8D6] to-[#F9F8F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-6">
            <p className="overline">Blog · Notizie</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Voci dal
              <br />
              <em className="text-[#0E7C8F]">Metodo.</em>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-lg font-light text-[#3A4F6B]">
              Riconoscimenti, eventi e riflessioni nate dal libro e dal percorso
              di formazione di Michele Roccaro.
            </p>
          </div>
        </div>

        {/* Featured article */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="grid md:grid-cols-12 gap-10 md:gap-16 border-t border-b border-[#E5E1DA] py-12 md:py-16"
          data-testid="blog-article-stella-esse"
        >
          {/* Image collage — editorial layout */}
          <div className="md:col-span-6">
            <div className="grid grid-cols-12 gap-3">
              {/* Main award photo */}
              <div className="col-span-12 aspect-[4/3] overflow-hidden border border-[#0B192C]/10">
                <img
                  src={heroImg}
                  alt="Premio La Stella di ESSE — Palazzo Valentini, Roma"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Pergamena (square, respects original ratio) */}
              <a
                href="https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/gn33iyi3_PERGAMENA%20DI%20ONOREFICENZA.png"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-pergamena"
                className="group col-span-7 aspect-square overflow-hidden border border-[#0B192C]/10 relative bg-[#F4E8D6]"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/gn33iyi3_PERGAMENA%20DI%20ONOREFICENZA.png"
                  alt="Pergamena ufficiale Premio La Stella di E.S.S.E. — Michele Roccaro, 13 giugno 2026"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0B192C]/0 group-hover:bg-[#0B192C]/30 transition-colors flex items-end justify-start p-3 opacity-0 group-hover:opacity-100">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6]">
                    Pergamena ufficiale ↗
                  </span>
                </div>
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#0B192C] text-[#F9F8F6] text-[9px] uppercase tracking-[0.18em]">
                  Pergamena
                </span>
              </a>
              {/* Ceremony handshake */}
              <div className="col-span-5 aspect-square overflow-hidden border border-[#0B192C]/10">
                <img
                  src={secondaryImg}
                  alt="Premiazione con il Presidente Massimo Di Mauro"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Article content */}
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#3A4F6B]">
              <Calendar size={12} />
              <span>Giugno 2026</span>
              <span className="text-[#D4B895]">·</span>
              <span>Riconoscimenti</span>
            </div>

            <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-5 leading-[1.1] text-[#0B192C]">
              Gratitudine, impegno e responsabilità: un riconoscimento che
              guarda al <em className="text-[#0E7C8F]">futuro</em>.
            </h3>

            <div className="mt-8 space-y-4 text-base font-light leading-relaxed text-[#3A4F6B] max-h-[420px] overflow-y-auto pr-2">
              <p>
                Con immenso onore e profonda gratitudine desidero condividere
                il prestigioso riconoscimento ricevuto durante l'Evento di
                Premiazione Nazionale <em>"La Stella di ESSE"</em>, dedicato ai
                valori dell'Innovazione, della Crescita Personale e della
                Formazione, svoltosi nella prestigiosa{" "}
                <strong className="text-[#0B192C] font-normal">
                  Palazzo Valentini, Sala del Consiglio
                </strong>
                .
              </p>
              <p>
                Ricevere questo premio rappresenta per me un momento di grande
                emozione e una conferma del percorso che da anni porto avanti
                nel campo dell'innovazione digitale, della formazione
                professionale, della crescita personale e della valorizzazione
                delle persone.
              </p>
              <p>
                Un sincero ringraziamento al Presidente Nazionale del Centro
                Studi E.S.S.E., <strong className="text-[#0B192C] font-normal">Dott. Massimo Di Mauro</strong>, per la fiducia accordatami
                e l'impegno nel promuovere eccellenze, competenze e percorsi di
                crescita personale e professionale. Grazie anche al
                Vicepresidente Vicario <strong className="text-[#0B192C] font-normal">Giuseppe Palma</strong> per il prezioso contributo nella
                diffusione dei valori dell'associazione.
              </p>
              <p>
                Ringrazio le autorevoli istituzioni presenti — On. Giuseppe
                Bicchielli, On. Mario Luciano Crea, On. Fabrizio Santori — la
                cui partecipazione è un forte segnale di attenzione verso
                formazione, innovazione e crescita delle persone, elementi
                fondamentali per costruire il futuro del nostro Paese.
              </p>
              <p>
                Questo riconoscimento è dedicato a tutte le persone che hanno
                creduto nel mio percorso: all'Associazione ACDT, ai
                collaboratori, agli amici e a tutti coloro che condividono una
                visione basata su etica, competenza, innovazione e crescita
                continua.
              </p>
              <p>
                Un pensiero speciale al libro <em>Metodo di Mare</em>, nato
                dall'esperienza vissuta tra mare, disciplina, leadership e
                trasformazione digitale, che continua a trasmettere valori e
                strumenti utili per affrontare le sfide del cambiamento.
              </p>
              <p className="font-serif-display italic text-xl text-[#0B192C]">
                "Questo premio non rappresenta un punto di arrivo, ma una nuova
                partenza."
              </p>
              <p>
                Continuerò a lavorare con passione e determinazione per
                contribuire alla diffusione della cultura digitale, della
                formazione professionale e della crescita personale, mettendo
                sempre al centro le persone e il valore delle relazioni.
              </p>
              <p className="pt-2 border-t border-[#E5E1DA] mt-6 text-[#0B192C]">
                <strong className="font-normal">Michele Roccaro</strong>
                <br />
                <span className="text-sm text-[#3A4F6B]">
                  Imprenditore Digitale · Formatore · Autore di Metodo di Mare
                </span>
              </p>
            </div>

            <a
              href="https://amzn.eu/d/0iLs59IO"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-cta-amazon"
              className="group inline-flex items-center gap-3 mt-8 px-7 py-3 border border-[#0B192C] text-sm uppercase tracking-[0.2em] text-[#0B192C] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
            >
              Scopri il libro su Amazon
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </motion.article>
      </div>
    </section>
  );
}
